// src/redux/slices/orderSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

/**
 * Signature of a filter set — used to detect when the user changes filters
 * mid-flight so we can drop stale responses and reset the page cache.
 */
const filterSignature = (filters = {}) => JSON.stringify({
  limit: filters.limit || 25,
  byOrderId: filters.byOrderId || "",
  bySellerId: filters.bySellerId || "",
  byUserId: filters.byUserId || "",
  byOrderStatus: filters.byOrderStatus || "",
  byDeliveryStatus: filters.byDeliveryStatus || "",
  byInvoiceId: filters.byInvoiceId || "",
  byProductId: filters.byProductId || "",
  byKeyword: filters.byKeyword || "",
  startDate: filters.startDate || "",
  endDate: filters.endDate || "",
  sortBy: filters.sortBy || "",
  sortOrder: filters.sortOrder || "",
});

const buildRequestBody = (filters, page) => ({
  limit: filters?.limit || 25,
  currentPage: page,
  byOrderId: filters?.byOrderId || "",
  bySellerId: filters?.bySellerId || "",
  byUserId: filters?.byUserId || "",
  byOrderStatus: filters?.byOrderStatus || "",
  byDeliveryStatus: filters?.byDeliveryStatus || "",
  byInvoiceId: filters?.byInvoiceId || "",
  byProductId: filters?.byProductId || "",
  byKeyword: filters?.byKeyword || "",
  startDate: filters?.startDate || "",
  endDate: filters?.endDate || "",
  sortBy: filters?.sortBy || "",
  sortOrder: filters?.sortOrder || "",
});

/**
 * Fetch a single page of orders. Pass `foreground: false` to fetch silently
 * into the page cache without touching `loading`/`orders`.
 */
export const fetchOrdersPage = createAsyncThunk(
  'orders/fetchOrdersPage',
  async ({ filters, page, foreground = true }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    const body = buildRequestBody(filters, page);
    const signature = filterSignature(body);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v2/orders/filter`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { signature, page, foreground, payload: response.data };
    } catch (error) {
      return rejectWithValue({
        signature,
        page,
        foreground,
        message: error.response?.data?.message || "Failed to fetch orders",
      });
    }
  }
);

// Backwards-compatible wrapper: dispatch(fetchOrders(filters)) still works.
export const fetchOrders = (filters) =>
  fetchOrdersPage({ filters, page: filters?.currentPage || 1, foreground: true });

const orderSlice = createSlice({
  name: 'orders',
  initialState: {
    orders: [],
    total: 0,
    totalPages: 1,
    currentPage: 1,
    pageCache: {},      // { [page: number]: orders[] } — valid for the current signature
    signature: '',      // signature of the filter set currently in pageCache
    loading: false,     // foreground fetch in progress
    prefetching: false, // background prefetch in progress
    error: null,
  },
  reducers: {
    /** Show a page that has already been prefetched, no API call. */
    showCachedPage(state, action) {
      const page = action.payload;
      if (state.pageCache[page]) {
        state.orders = state.pageCache[page];
        state.currentPage = page;
        state.loading = false;
      }
    },
    /** Mark/unmark the background prefetch as running. */
    setPrefetching(state, action) {
      state.prefetching = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrdersPage.pending, (state, action) => {
        const { foreground, filters } = action.meta.arg;
        const incoming = filterSignature(filters);
        // Filter set changed — discard any cached pages from the old filter.
        if (incoming !== state.signature) {
          state.pageCache = {};
          state.signature = incoming;
          state.total = 0;
          state.totalPages = 1;
        }
        if (foreground) {
          state.loading = true;
          state.error = null;
        }
      })
      .addCase(fetchOrdersPage.fulfilled, (state, action) => {
        const { signature, page, foreground, payload } = action.payload;
        // Discard stale response (filters changed mid-flight).
        if (signature !== state.signature) {
          if (foreground) state.loading = false;
          return;
        }
        const list = payload?.data || [];
        state.pageCache[page] = list;
        state.total = payload?.total ?? state.total;
        state.totalPages = payload?.totalPages ?? state.totalPages;
        if (foreground) {
          state.orders = list;
          state.currentPage = page;
          state.loading = false;
        }
      })
      .addCase(fetchOrdersPage.rejected, (state, action) => {
        const meta = action.meta?.arg || {};
        if (meta.foreground) {
          state.loading = false;
          state.error = action.payload?.message || 'Failed to fetch orders';
        }
      });
  },
});

export const { showCachedPage, setPrefetching } = orderSlice.actions;
export default orderSlice.reducer;
